import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { openApi } from "../../services/api";

// ─── Fetch app name from public settings (no auth required) ───────────────────
async function fetchAppName() {
  try {
    const { data } = await openApi.get("/public-settings/appName");
    return data || "Fenil CRM";
  } catch {
    return "Fenil CRM";
  }
}

// ─── Last updated date ─────────────────────────────────────────────────────────
const LAST_UPDATED = "23 de abril de 2025";

// ─── Section definitions ───────────────────────────────────────────────────────
const buildSections = (appName) => [
  {
    id: "sobre",
    title: "Sobre este Aplicativo",
    highlight: true,
    content: [
      `${appName} é uma plataforma de gestão de atendimentos e relacionamento com clientes (CRM) que permite às empresas gerenciar conversas via WhatsApp, automatizar fluxos de atendimento, capturar e gerenciar leads provenientes do Facebook Lead Ads, e acompanhar o pipeline de vendas por meio de um quadro Kanban.`,
      `Esta Política de Privacidade descreve como o ${appName} coleta, utiliza, armazena e protege as informações dos usuários da plataforma e dos dados obtidos por meio de integrações com plataformas de terceiros, incluindo a Meta (Facebook/Instagram).`,
      `Esta política se aplica a todos os usuários da plataforma ${appName} e está em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD) e com os Termos da Plataforma da Meta.`,
    ],
  },
  {
    id: "dados-coletados",
    title: "1. Dados Coletados",
    content: [
      `O ${appName} coleta as seguintes categorias de informações:`,
      `**Dados de conta do usuário:** nome completo, endereço de e-mail, senha (armazenada com criptografia), número de telefone e cargo dentro da empresa.`,
      `**Dados de atendimento:** histórico de conversas via WhatsApp, arquivos de mídia (imagens, áudios, vídeos, documentos) trocados durante atendimentos, e anotações inseridas pelos agentes.`,
      `**Dados de contatos/leads:** nome, número de telefone, e-mail, e demais informações inseridas manualmente ou importadas de integrações.`,
      `**Dados provenientes do Facebook/Meta:** ao integrar o ${appName} com o Facebook Lead Ads, a plataforma acessa, por meio das permissões concedidas pelo usuário administrador:`,
      `• **pages_show_list** – lista das Páginas do Facebook gerenciadas pelo usuário, utilizada exclusivamente para vincular a página correta à conta de integração do ${appName};\n• **leads_retrieval** – dados dos formulários de lead preenchidos por potenciais clientes nas campanhas de Lead Ads, incluindo: nome, e-mail, telefone e respostas a perguntas personalizadas do formulário.`,
      `Esses dados do Facebook são coletados apenas após autorização explícita do administrador da conta e são utilizados somente para os fins descritos nesta política.`,
      `**Dados de uso e logs:** endereço IP, data e hora de acesso, tipo de dispositivo/navegador, e ações realizadas dentro da plataforma, para fins de segurança e auditoria.`,
    ],
  },
  {
    id: "uso-dados",
    title: "2. Como Utilizamos os Dados",
    content: [
      `Os dados coletados pelo ${appName} são utilizados exclusivamente para:`,
      `• **Operação da plataforma:** autenticação, gerenciamento de atendimentos, comunicação com contatos e funcionamento dos recursos da plataforma;\n• **Integração com Facebook Lead Ads:** sincronizar automaticamente leads capturados via formulários do Facebook com o CRM, permitindo que a equipe de atendimento responda rapidamente;\n• **Automações de fluxo:** executar sequências automáticas de mensagens e ações configuradas pelo administrador;\n• **Relatórios e análises:** gerar métricas de desempenho para os gestores da empresa;\n• **Segurança e conformidade:** detectar e prevenir uso fraudulento, e cumprir obrigações legais.`,
      `Os dados provenientes do Facebook (leads e informações de páginas) **nunca são compartilhados com terceiros, vendidos ou utilizados para fins publicitários**. Seu uso é restrito à operação interna do CRM da empresa que realizou a integração.`,
    ],
  },
  {
    id: "facebook-meta",
    title: "3. Integração com Facebook / Meta",
    highlight: false,
    content: [
      `O ${appName} utiliza a API do Facebook para oferecer o recurso de captura automática de leads via Facebook Lead Ads. Esta integração exige as seguintes permissões:`,
      `**pages_show_list:** permite que o ${appName} liste as Páginas do Facebook associadas à conta do usuário administrador, para que ele possa selecionar qual página deseja integrar. Esta permissão é utilizada exclusivamente no momento da configuração e não acessa publicações, mensagens ou dados privados das páginas.`,
      `**leads_retrieval:** permite que o ${appName} acesse os leads enviados por meio de formulários de Lead Ads vinculados às páginas selecionadas pelo administrador. Os dados acessados são apenas aqueles que o usuário preencheu voluntariamente no formulário de anúncio do Facebook.`,
      `O acesso aos dados do Facebook é realizado por meio da API oficial do Facebook for Developers, com tokens de acesso gerados pelo próprio usuário administrador durante o processo de configuração. O ${appName} armazena apenas o token de acesso necessário para manter a integração ativa, e este token pode ser revogado a qualquer momento pelo usuário nas configurações da integração ou diretamente na Central de Configurações do Facebook.`,
      `O ${appName} segue todos os requisitos da Política de Dados da Meta e dos Termos da Plataforma da Meta, incluindo restrições sobre o uso, compartilhamento e retenção de dados obtidos via APIs do Facebook.`,
    ],
  },
  {
    id: "compartilhamento",
    title: "4. Compartilhamento de Dados",
    content: [
      `O ${appName} **não vende, aluga nem comercializa** dados pessoais de usuários ou de seus clientes. O compartilhamento de dados ocorre apenas nas seguintes situações:`,
      `**Provedores de infraestrutura:** serviços de hospedagem e banco de dados que suportam o funcionamento técnico da plataforma, mediante contratos de confidencialidade e proteção de dados.`,
      `**Integrações configuradas pelo usuário:** quando o administrador conecta canais externos (WhatsApp, Facebook, webhooks), os dados são transmitidos conforme a configuração realizada pelo próprio usuário.`,
      `**Obrigação legal:** quando exigido por lei, ordem judicial ou autoridade competente, e somente na extensão necessária.`,
    ],
  },
  {
    id: "retencao",
    title: "5. Retenção e Exclusão de Dados",
    content: [
      `Os dados são mantidos pelo período necessário para a prestação dos serviços:`,
      `• **Dados de conta:** enquanto a conta estiver ativa e por até 90 dias após o encerramento;\n• **Leads do Facebook:** conforme configurado pela empresa; o administrador pode excluí-los a qualquer momento;\n• **Histórico de atendimentos:** conforme configurado pelo administrador da empresa;\n• **Logs de segurança:** por até 12 meses;\n• **Dados financeiros:** pelo período exigido pela legislação fiscal aplicável.`,
      `Após o período de retenção, os dados são excluídos de forma segura ou anonimizados.`,
    ],
  },
  {
    id: "exclusao",
    title: "6. Exclusão de Dados — Dados do Facebook",
    highlight: true,
    content: [
      `Em conformidade com os requisitos da Meta Plataforma, o ${appName} oferece mecanismos para exclusão dos dados obtidos via integração com o Facebook:`,
      `**Como solicitar a exclusão dos seus dados do Facebook no ${appName}:**`,
      `1. Acesse as Configurações da plataforma ${appName} como administrador;\n2. Navegue até a seção "Integrações" → "Facebook Lead Ads";\n3. Clique em "Revogar integração" para desconectar sua conta do Facebook e interromper a sincronização;\n4. Para excluir os leads já importados, utilize o recurso de exclusão em massa disponível na seção de Contatos;\n5. Para solicitar a exclusão completa de todos os dados vinculados à sua conta do Facebook, envie um e-mail para o suporte da plataforma com o assunto "Solicitação de Exclusão de Dados Facebook".`,
      `Também é possível revogar as permissões concedidas ao ${appName} diretamente no Facebook:`,
      `1. Acesse facebook.com/settings → "Apps e sites";\n2. Localize o ${appName} na lista;\n3. Clique em "Remover" para revogar todas as permissões concedidas.`,
      `Após a revogação, o ${appName} encerrará imediatamente a sincronização de novos dados. Os dados já importados podem ser excluídos conforme os passos acima.`,
    ],
  },
  {
    id: "seguranca",
    title: "7. Segurança dos Dados",
    content: [
      `O ${appName} adota medidas técnicas e organizacionais para proteger os dados contra acesso não autorizado, perda ou divulgação indevida:`,
      `• Criptografia em trânsito (HTTPS/TLS) para todas as comunicações;\n• Senhas armazenadas com hash seguro (nunca em texto claro);\n• Controle de acesso baseado em perfis (administrador, supervisor, agente);\n• Monitoramento de atividades suspeitas e logs de auditoria;\n• Tokens de integração armazenados de forma criptografada.`,
      `Em caso de incidente de segurança que afete dados pessoais, os usuários afetados serão notificados em conformidade com os prazos estabelecidos pela LGPD.`,
    ],
  },
  {
    id: "direitos",
    title: "8. Seus Direitos (LGPD)",
    content: [
      `Em conformidade com a LGPD (Lei nº 13.709/2018), você tem direito a:`,
      `• **Acesso:** solicitar confirmação e cópia dos seus dados tratados;\n• **Correção:** solicitar atualização de dados incompletos ou incorretos;\n• **Exclusão:** solicitar a remoção de dados tratados sem base legal adequada;\n• **Portabilidade:** solicitar transferência dos seus dados a outro fornecedor;\n• **Revogação do consentimento:** retirar consentimentos a qualquer momento;\n• **Informação:** saber com quais terceiros seus dados são compartilhados.`,
      `Para exercer esses direitos, entre em contato com o administrador da sua conta ou pelo canal de suporte da plataforma.`,
    ],
  },
  {
    id: "cookies",
    title: "9. Cookies e Armazenamento Local",
    content: [
      `O ${appName} utiliza cookies e armazenamento local (localStorage) exclusivamente para:`,
      `• Manter a sessão autenticada do usuário;\n• Armazenar preferências de interface (tema, idioma);\n• Garantir o funcionamento correto da aplicação.`,
      `Não são utilizados cookies para rastreamento comportamental, publicidade ou compartilhamento com redes de anúncios.`,
    ],
  },
  {
    id: "alteracoes",
    title: "10. Alterações nesta Política",
    content: [
      `Esta Política de Privacidade pode ser atualizada periodicamente. Em caso de alterações relevantes, os usuários serão notificados por e-mail ou por aviso visível na plataforma. A data de última atualização está sempre indicada no topo desta página.`,
      `O uso contínuo da plataforma após a data de vigência das alterações implica a aceitação da versão atualizada.`,
    ],
  },
  {
    id: "contato",
    title: "11. Contato e Encarregado de Dados (DPO)",
    content: [
      `Para dúvidas, solicitações ou reclamações relacionadas a esta Política de Privacidade ou ao tratamento dos seus dados pessoais, entre em contato pelo suporte interno da plataforma ${appName}.`,
      `Para solicitações relacionadas especificamente aos dados obtidos via integração com o Facebook/Meta, utilize o processo descrito na seção 6 desta política.`,
    ],
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────
export default function PrivacyPolicy() {
  const [appName, setAppName] = useState("Fenil CRM");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchAppName().then((name) => {
      setAppName(name);
      setLoaded(true);
    });
  }, []);

  const sections = buildSections(appName);

  return (
    <div style={styles.page}>
      {/* ── Top navigation bar ── */}
      <div style={styles.topBar}>
        <div style={styles.topBarInner}>
          <div style={styles.brand}>
            <div style={styles.logoFallback}>{appName.charAt(0)}</div>
            <span style={styles.brandName}>{appName}</span>
          </div>
          <div style={styles.navLinks}>
            <RouterLink to="/politica-de-privacidade" style={{ ...styles.navLink, ...styles.navActive }}>
              Privacidade
            </RouterLink>
            <RouterLink to="/termos-de-servico" style={styles.navLink}>
              Termos de Serviço
            </RouterLink>
            <RouterLink to="/login" style={styles.loginBtn}>
              ← Entrar na plataforma
            </RouterLink>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <div style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.heroBadge}>Política de Privacidade</div>
          <h1 style={styles.heroTitle}>{appName} — Privacidade e Proteção de Dados</h1>
          <p style={styles.heroMeta}>
            Última atualização: <strong>{LAST_UPDATED}</strong>
            &nbsp;·&nbsp;
            Plataforma: <strong>{appName}</strong>
          </p>
          <p style={styles.heroDesc}>
            Esta política descreve como o <strong>{appName}</strong> coleta, utiliza e
            protege seus dados pessoais e os dados obtidos por meio da integração com
            o Facebook / Meta, em conformidade com a LGPD e os Termos da Plataforma da Meta.
          </p>
        </div>
      </div>

      {/* ── Main layout ── */}
      <div style={styles.layout}>
        {/* Sidebar TOC */}
        <aside style={styles.toc}>
          <p style={styles.tocHeading}>Sumário</p>
          <a href="#sobre" style={styles.tocLink} onClick={smoothScroll("#sobre")}>Sobre o Aplicativo</a>
          {sections.filter(s => s.id !== "sobre").map(s => (
            <a key={s.id} href={`#${s.id}`} style={styles.tocLink} onClick={smoothScroll(`#${s.id}`)}>
              {s.title}
            </a>
          ))}
        </aside>

        {/* Content */}
        <main style={styles.main}>
          {/* App identity card — always visible, critical for Meta review */}
          <div style={styles.identityCard}>
            <div style={styles.identityIcon}>🏢</div>
            <div>
              <p style={styles.identityTitle}>Esta Política de Privacidade pertence ao aplicativo <strong>{appName}</strong></p>
              <p style={styles.identityDesc}>
                {appName} é uma plataforma de CRM e gestão de atendimentos que integra
                WhatsApp e Facebook Lead Ads para empresas. Esta página é pública e
                acessível sem necessidade de login.
              </p>
            </div>
          </div>

          {/* Meta integration callout */}
          <div style={styles.metaCard}>
            <div style={styles.metaCardHeader}>
              <span style={{ fontSize: 18 }}>🔗</span>
              <strong>Integração com Facebook / Meta</strong>
            </div>
            <p style={styles.metaCardText}>
              O <strong>{appName}</strong> solicita as permissões{" "}
              <code style={styles.code}>pages_show_list</code> e{" "}
              <code style={styles.code}>leads_retrieval</code> para sincronizar leads
              do Facebook Lead Ads com o CRM. Veja a seção 3 para detalhes completos
              sobre como esses dados são usados, e a seção 6 para instruções de exclusão.
            </p>
          </div>

          {/* Sections */}
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              style={{
                ...styles.section,
                ...(section.highlight ? styles.sectionHighlight : {}),
              }}
            >
              <h2 style={styles.sectionTitle}>{section.title}</h2>
              {section.content.map((para, i) => (
                <p key={i} style={styles.para}>
                  {renderMarkdown(para)}
                </p>
              ))}
            </div>
          ))}

          {/* Footer note */}
          <div style={styles.footerNote}>
            <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
              Esta Política de Privacidade é específica para o aplicativo{" "}
              <strong>{appName}</strong> e não se aplica a outros serviços ou
              plataformas de terceiros integrados. Consulte as políticas de privacidade
              do Facebook em{" "}
              <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1" }}>
                facebook.com/privacy/policy
              </a>
              .
            </p>
          </div>
        </main>
      </div>

      {/* ── Footer ── */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <span style={styles.footerText}>
            © {new Date().getFullYear()} {appName}. Todos os direitos reservados.
          </span>
          <div style={styles.footerLinks}>
            <RouterLink to="/politica-de-privacidade" style={styles.footerLink}>Política de Privacidade</RouterLink>
            <span style={{ color: "#475569" }}>·</span>
            <RouterLink to="/termos-de-servico" style={styles.footerLink}>Termos de Serviço</RouterLink>
            <span style={{ color: "#475569" }}>·</span>
            <RouterLink to="/login" style={styles.footerLink}>Login</RouterLink>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function smoothScroll(id) {
  return (e) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

function renderMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={i} style={styles.code}>{part.slice(1, -1)}</code>;
    }
    // handle bullet lists (\n• ...)
    if (part.includes("\n•")) {
      return (
        <span key={i}>
          {part.split("\n").map((line, j) => (
            <span key={j} style={{ display: "block", marginBottom: line.startsWith("•") ? 4 : 0 }}>
              {line}
            </span>
          ))}
        </span>
      );
    }
    // handle numbered lists (\n1. ...)
    if (part.includes("\n1.") || part.match(/\n\d+\./)) {
      return (
        <span key={i}>
          {part.split("\n").map((line, j) => (
            <span key={j} style={{ display: "block", marginBottom: 4 }}>
              {line}
            </span>
          ))}
        </span>
      );
    }
    return part;
  });
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const PURPLE = "#6366f1";

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#1e293b",
    lineHeight: 1.75,
  },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 200,
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #e2e8f0",
  },
  topBarInner: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "0 24px",
    height: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  brand: { display: "flex", alignItems: "center", gap: 10 },
  logoFallback: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: PURPLE,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 15,
    flexShrink: 0,
  },
  brandName: { fontWeight: 700, fontSize: 15, color: "#0f172a" },
  navLinks: { display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" },
  navLink: {
    fontSize: 13,
    fontWeight: 500,
    color: "#64748b",
    textDecoration: "none",
    borderBottom: "2px solid transparent",
    paddingBottom: 2,
  },
  navActive: { color: PURPLE, borderBottomColor: PURPLE },
  loginBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: PURPLE,
    textDecoration: "none",
    padding: "6px 14px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },
  hero: {
    background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
    padding: "60px 24px 52px",
  },
  heroInner: { maxWidth: 760, margin: "0 auto", textAlign: "center" },
  heroBadge: {
    display: "inline-block",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "4px 14px",
    borderRadius: 20,
    marginBottom: 16,
    border: "1px solid rgba(255,255,255,0.35)",
  },
  heroTitle: {
    fontSize: "clamp(22px, 3.5vw, 34px)",
    fontWeight: 800,
    color: "#fff",
    margin: "0 0 10px",
    lineHeight: 1.25,
  },
  heroMeta: { fontSize: 13, color: "rgba(255,255,255,0.72)", margin: "0 0 14px" },
  heroDesc: { fontSize: 15, color: "rgba(255,255,255,0.9)", margin: 0, lineHeight: 1.65 },
  layout: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "40px 24px 48px",
    display: "flex",
    gap: 40,
    alignItems: "flex-start",
  },
  toc: {
    width: 210,
    flexShrink: 0,
    position: "sticky",
    top: 72,
    backgroundColor: "#fff",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  tocHeading: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#94a3b8",
    margin: "0 0 8px",
    padding: "0 5px",
  },
  tocLink: {
    fontSize: 12,
    color: "#64748b",
    textDecoration: "none",
    padding: "4px 7px",
    borderRadius: 5,
    display: "block",
    lineHeight: 1.4,
  },
  main: { flex: 1, minWidth: 0 },
  identityCard: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    backgroundColor: "#ede9fe",
    border: "1px solid #c4b5fd",
    borderRadius: 10,
    padding: "18px 22px",
    marginBottom: 16,
  },
  identityIcon: { fontSize: 28, flexShrink: 0, marginTop: 2 },
  identityTitle: { fontWeight: 700, fontSize: 14, color: "#3730a3", margin: "0 0 6px" },
  identityDesc: { fontSize: 13.5, color: "#4338ca", margin: 0, lineHeight: 1.6 },
  metaCard: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 10,
    padding: "16px 20px",
    marginBottom: 20,
  },
  metaCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9a3412",
    marginBottom: 8,
  },
  metaCardText: { fontSize: 13.5, color: "#7c2d12", margin: 0, lineHeight: 1.65 },
  code: {
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontFamily: "monospace",
    fontSize: 12,
    padding: "2px 6px",
    borderRadius: 4,
    border: "1px solid #e2e8f0",
  },
  section: {
    backgroundColor: "#fff",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    padding: "24px 28px",
    marginBottom: 14,
    scrollMarginTop: 76,
  },
  sectionHighlight: {
    borderColor: "#a5b4fc",
    backgroundColor: "#fafafe",
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 14px",
    paddingBottom: 10,
    borderBottom: "2px solid #f1f5f9",
  },
  para: {
    fontSize: 14,
    color: "#374151",
    marginBottom: 12,
    lineHeight: 1.75,
  },
  footerNote: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "14px 18px",
    marginTop: 8,
  },
  footer: { backgroundColor: "#0f172a", padding: "22px 24px" },
  footerInner: {
    maxWidth: 1120,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
  },
  footerText: { fontSize: 13, color: "#94a3b8" },
  footerLinks: { display: "flex", alignItems: "center", gap: 8 },
  footerLink: { fontSize: 13, color: "#94a3b8", textDecoration: "none" },
};
